import { Component, signal, DestroyRef, inject, output, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location, UpperCasePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Commands } from '../../services/commands/commands';
import { Events } from '../../services/events/events';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-room-setup',
  imports: [UpperCasePipe],
  templateUrl: './room-setup.html',
  styleUrl: './room-setup.css',
})
export class RoomSetup {

  // PROPERTIES

  private destroyRef = inject(DestroyRef);
  private base_path?: string;

  // SIGNALS

  protected room_id = signal<string>('');
  protected is_room_url_copied = signal<boolean>(false);

  protected is_initial_step = signal<boolean>(false);
  protected is_join_step = signal<boolean>(false);
  protected is_joining = signal<boolean>(false);
  
  protected manual_room_id = signal<string>('');
  protected is_manual_room_id_valid = signal<boolean>(true);

  // OUTPUTS

  public connectionCallback = output<void>();
  public roomIdListener = output<string>();

  // ROOM JOINING MANAGEMENT

  private joinRoom(room_id: string | null, invalidRoomCallback?: Function): void{

    this.commands.enterGame(room_id?.toLowerCase() ?? null, () => {

      this.connectToRoom();

      // ! IMPORTANT !
      // Even if room id is provided via URL parameter
      // set the room id only after receiving the BE response
      // to prevent XSS attack

      this.room_id.set(this.commands.getRoomId() ?? "");

      // always rewrite the url with the proper room id
      this.location.replaceState(`/${this.base_path}/waiting-room/${this.room_id()}`);

      // in case the room has been typed manually, show the waiting room
      this.is_join_step.set(false);

    }, invalidRoomCallback);

  }

  private connectToRoom(): void{

    this.events.connect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.connectionCallback.emit();
        }
      });

  }

  // ROOM JOINING CHOICE

  public createNewRoom(): void{

    this.is_initial_step.set(false);
    this.joinRoom(null);

  }

  public showJoinInput(): void{

    this.is_initial_step.set(false);
    this.is_join_step.set(true);

  }

  public updateManualRoomId(event: Event): void{

    const input = event.target as HTMLInputElement;
    this.manual_room_id.set(input.value);
    this.is_manual_room_id_valid.set(true);

  }

  public submitManualRoomId(): void{

    const room_id = this.manual_room_id().trim();
    if (!room_id) return;

    this.is_joining.set(true);

    this.joinRoom(room_id, () => {
      this.is_joining.set(false);
      this.is_manual_room_id_valid.set(false);
    });

  }

  public showInitialStep(){

    this.is_join_step.set(false);
    this.is_initial_step.set(true);

    // add the following when join room request cancel is added in this method
    //this.is_manual_room_id_valid.set(true);
    //this.manual_room_id.set('');
    //this.is_joining.set(false);

  }

  // ROOM CTAs

  public refreshRoom(): void{
    window.location.href = `/${this.base_path}/waiting-room`;
  }

  public copyRoomUrl(): void{

    // no check needed: navigator.clipboard basically always available

    const room_url = `${environment.base_url}/ml/${this.room_id()}`;

    navigator.clipboard.writeText(room_url)
      .then(() => {
        this.is_room_url_copied.set(true);
      })
      .catch(() => {
        // to avoid ugly errors on console
        console.log("[WEB API Error - Clipboard] No panic! For some reason the browser didn't copy the content.");
      });

  }

  public shareRoomUrl(): void{

    if (!navigator.share) return; // old browser do not support this web api

    const room_url = `${environment.base_url}/ml/${this.room_id()}`;

    navigator.share({ url: room_url })
      .catch(() => {
        // to avoid ugly errors on console
        console.log("[WEB API Error - Share] No panic! User probably cancelled the share dialog.");
      });

  }

  // LIFECYCLE

  constructor(
    private commands: Commands,
    private events: Events,
    private location: Location,
    private activated_route: ActivatedRoute
  ){

    this.base_path = activated_route.parent?.snapshot.url[0]?.path;

    effect(() => {
      this.roomIdListener.emit(this.room_id());
    });

  }

  ngOnInit(): void{

    const room_id = this.activated_route.snapshot.paramMap.get("room_id");

    if (room_id){

      this.joinRoom(room_id, () => {
        alert("Ops, seems like the room you are trying to join does not exists");
        this.is_initial_step.set(true);
      });

      return;

    }
    
    this.is_initial_step.set(true);

  }

}
